import {
	createContext,
	useContext,
	useMemo,
	FC,
	ReactNode,
	useState,
	useCallback,
	useEffect,
} from 'react';
import {
	Application,
	Feedback,
	FunctionaryFeedbackRequest,
	UserRole,
} from '../../../shared/VolunteerApplicationServiceTypes';
import { submitFeedbackFromProposer } from '../../../service/VolunteerApplicationService';
import { useToastMessage } from '../../../hooks/useToastMessage';
import { useApplicationId } from '../../../hooks/useApplicationId';
import { useAuth } from '../../../hooks/useAuth';
import { INTERVIEW_LANDING } from '../../../shared/Routes';
import { useInterviewWizard } from '../useInterviewWizard';
import { Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import {
	addMandatoryQuestionsToFunctionaryFeedback,
	transformToAnswersForMandatoryQuestionsFromGRPC,
} from '../../../shared/Utils';
import { useInfoPopup } from '../../../hooks/useInfoPopUp';
import ConfirmTick from '../../../assets/images/confirm-tick.svg';
import { ExclamationCircleFilled } from '@ant-design/icons';
import useUserSession from '../../../hooks/useUserSession';

export interface ApplicantInterviewContextType {
	inspirationAndWillingness: Feedback;
	setInspirationAndWillingness: (feedback: Feedback) => void;
	practice: Feedback;
	setPractice: (feedback: Feedback) => void;
	characterAndHabits: Feedback;
	setCharacterAndHabits: (feedback: Feedback) => void;
	availabilityAfterProgram: Feedback;
	setAvailabilityAfterProgram: (feedback: Feedback) => void;
	support: Feedback;
	setSupport: (feedback: Feedback) => void;
	understandingOfHeartfulness: Feedback;
	setUnderstandingOfHeartfulness: (feedback: Feedback) => void;
	eligibilityCriteria: Feedback;
	setEligibilityCriteria: (feedback: Feedback) => void;
	dietaryHabitsAndHealth: Feedback;
	setDietaryHabitsAndHealth: (feedback: Feedback) => void;
	application: Partial<Application>;
	onSubmitClick: () => void;
	onCancelClick: () => void;
}

interface Props {
	children: ReactNode;
}

const ApplicantInterviewContext = createContext<ApplicantInterviewContextType>({
	inspirationAndWillingness: {},
	setInspirationAndWillingness: () => {},
	practice: {},
	setPractice: () => {},
	characterAndHabits: {},
	setCharacterAndHabits: () => {},
	availabilityAfterProgram: {},
	setAvailabilityAfterProgram: () => {},
	support: {},
	setSupport: () => {},
	understandingOfHeartfulness: {},
	setUnderstandingOfHeartfulness: () => {},
	eligibilityCriteria: {},
	setEligibilityCriteria: () => {},
	dietaryHabitsAndHealth: {},
	setDietaryHabitsAndHealth: () => {},
	application: {},
	onSubmitClick: () => {},
	onCancelClick: () => {},
});

export const ApplicantInterviewProvider: FC<Props> = ({ children }: Props) => {
	const { i18n, t } = useTranslation();
	const { srcmId } = useAuth();
	const { showToastMessage } = useToastMessage();
	const [inspirationAndWillingness, setInspirationAndWillingness] =
		useState<Feedback>({});
	const [practice, setPractice] = useState<Feedback>({});
	const [characterAndHabits, setCharacterAndHabits] = useState<Feedback>({});
	const [availabilityAfterProgram, setAvailabilityAfterProgram] =
		useState<Feedback>({});
	const [support, setSupport] = useState<Feedback>({});
	const [understandingOfHeartfulness, setUnderstandingOfHeartfulness] =
		useState<Feedback>({});
	const [eligibilityCriteria, setEligibilityCriteria] = useState<Feedback>({});
	const [dietaryHabitsAndHealth, setDietaryHabitsAndHealth] =
		useState<Feedback>({});
	const [application, setApplication] = useState<Partial<Application>>({});
	const {
		applicationId,
		navigateWithApplicationId,
		fetchApplication: callFetchApplication,
	} = useApplicationId();
	const { confirmAndSave, onCancelClick, callApi } = useInterviewWizard();
	const { selectedRole } = useUserSession();
	const { infoPopUpAction } = useInfoPopup();

	const saveFeedback = useCallback(async () => {
		const feedback: FunctionaryFeedbackRequest = {
			applicationId,
			categoryReviewFeedback: {
				availabilityAfterProgram,
				characterAndHabits,
				inspirationAndWillingness,
				practice,
				support,
				understandingOfHeartfulness,
				eligibilityCriteria,
				dietaryHabitsAndHealth,
			},
			feedbackCategory: 'APPLICANT_INTERVIEW',
			functionarySrcmId: srcmId as string,
			selectedRoleCode: selectedRole as UserRole,
		};

		const feedbackWithMandatoryQuestionsAndAnswers =
			addMandatoryQuestionsToFunctionaryFeedback(feedback, i18n.language);

		try {
			await callApi(
				() =>
					submitFeedbackFromProposer(feedbackWithMandatoryQuestionsAndAnswers),
				'errorOccurredWhileSavingFeedback'
			);
			infoPopUpAction(
				'savedSuccessfully',
				'',
				() => {
					navigateWithApplicationId(INTERVIEW_LANDING);
				},
				ConfirmTick
			);
		} catch (e) {
			/* empty */
		}
	}, [
		applicationId,
		availabilityAfterProgram,
		characterAndHabits,
		inspirationAndWillingness,
		practice,
		support,
		understandingOfHeartfulness,
		eligibilityCriteria,
		dietaryHabitsAndHealth,
		srcmId,
		selectedRole,
		i18n.language,
		callApi,
		infoPopUpAction,
		navigateWithApplicationId,
	]);

	const onSubmitClick = useCallback(() => {
		confirmAndSave(saveFeedback);
	}, [confirmAndSave, saveFeedback]);

	const value = useMemo<ApplicantInterviewContextType>(() => {
		return {
			inspirationAndWillingness,
			setInspirationAndWillingness,
			practice,
			setPractice,
			characterAndHabits,
			setCharacterAndHabits,
			availabilityAfterProgram,
			setAvailabilityAfterProgram,
			support,
			setSupport,
			understandingOfHeartfulness,
			setUnderstandingOfHeartfulness,
			eligibilityCriteria,
			setEligibilityCriteria,
			dietaryHabitsAndHealth,
			setDietaryHabitsAndHealth,
			application,
			onSubmitClick,
			onCancelClick,
		};
	}, [
		inspirationAndWillingness,
		practice,
		characterAndHabits,
		availabilityAfterProgram,
		support,
		understandingOfHeartfulness,
		eligibilityCriteria,
		dietaryHabitsAndHealth,
		application,
		onSubmitClick,
		onCancelClick,
	]);

	const fetchApplication = useCallback(async () => {
		if (!applicationId) {
			showToastMessage('error', 'applicationId is missing');
		}

		try {
			const fetchedApplication = await callFetchApplication();

			const applicantInterviewFeedbackWithAnswersForMandatoryQuestions =
				transformToAnswersForMandatoryQuestionsFromGRPC(
					fetchedApplication.applicantInterviewFeedback
				);

			if (
				!inspirationAndWillingness ||
				Object.keys(inspirationAndWillingness).length === 0
			) {
				setInspirationAndWillingness(
					applicantInterviewFeedbackWithAnswersForMandatoryQuestions?.inspirationAndWillingness ??
						{}
				);
			}

			if (!practice || Object.keys(practice).length === 0) {
				setPractice(
					applicantInterviewFeedbackWithAnswersForMandatoryQuestions?.practice ??
						{}
				);
			}

			if (
				!availabilityAfterProgram ||
				Object.keys(availabilityAfterProgram).length === 0
			) {
				setAvailabilityAfterProgram(
					applicantInterviewFeedbackWithAnswersForMandatoryQuestions?.availabilityAfterProgram ??
						{}
				);
			}

			if (!characterAndHabits || Object.keys(characterAndHabits).length === 0) {
				setCharacterAndHabits(
					applicantInterviewFeedbackWithAnswersForMandatoryQuestions?.characterAndHabits ??
						{}
				);
			}

			if (!support || Object.keys(support).length === 0) {
				setSupport(
					applicantInterviewFeedbackWithAnswersForMandatoryQuestions?.support ??
						{}
				);
			}

			if (
				!understandingOfHeartfulness ||
				Object.keys(understandingOfHeartfulness).length === 0
			) {
				setUnderstandingOfHeartfulness(
					applicantInterviewFeedbackWithAnswersForMandatoryQuestions?.understandingOfHeartfulness ??
						{}
				);
			}

			if (
				!dietaryHabitsAndHealth ||
				Object.keys(dietaryHabitsAndHealth).length === 0
			) {
				setDietaryHabitsAndHealth(
					applicantInterviewFeedbackWithAnswersForMandatoryQuestions?.dietaryHabitsAndHealth ??
						{}
				);
			}

			if (
				!eligibilityCriteria ||
				Object.keys(eligibilityCriteria).length === 0
			) {
				setEligibilityCriteria(
					applicantInterviewFeedbackWithAnswersForMandatoryQuestions?.eligibilityCriteria ??
						{}
				);
			}

			setApplication(fetchedApplication);
		} catch (e) {
			// Do Nothing
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [applicationId, callFetchApplication, showToastMessage]);

	useEffect(() => {
		void fetchApplication();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fetchApplication]);

	return (
		<ApplicantInterviewContext.Provider value={value}>
			{selectedRole === UserRole.SNP_ADMIN && (
				<Alert
					message={
						<div className="text-center w-full">
							<ExclamationCircleFilled className="text-warning" />{' '}
							{t('adminsAreNotAllowed')}
						</div>
					}
					type="warning"
				/>
			)}
			{application?.isApprover && (
				<Alert
					message={
						<div className="text-center w-full">
							<ExclamationCircleFilled className="text-warning" />{' '}
							{t('approversAreNotAllowed')}
						</div>
					}
					type="warning"
				/>
			)}
			{selectedRole === UserRole.ZC && (
				<Alert
					message={
						<div className="text-center w-full">
							<ExclamationCircleFilled className="text-warning" />{' '}
							{t('zcIsNotAllowed')}
						</div>
					}
					type="warning"
				/>
			)}
			{children}
		</ApplicantInterviewContext.Provider>
	);
};

export const useApplicantInterviewWizard = () => {
	return useContext(ApplicantInterviewContext);
};
